import React, { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setAuthUser } from "./store/reducer/auth/authSlice";
import MainLayout from "./components/Layout/MainLayout";
import PrivateRoutes from "./middleware/PrivateRoute";
import ProtectedRoute from "./components/Auth/ProtectedRoute";

// Auth Pages
import Login from "./pages/Auth/Login";
import ResetPassword from "./pages/Auth/ResetPassword";
import ForgotPassword from "./pages/Auth/ForgotPassword";

// Module Pages
import Dashboard from "./pages/Dashboard";
import ListOpportunity from "./pages/Opportunity/ListOpportunity";
import AddOpportunity from "./pages/Opportunity/AddOpportunity";
import EditOpportunity from "./pages/Opportunity/EditOpportunity";
import OpportunitySummary from "./pages/OpportunitySummary/OpportunitySummary";

// Drawing Pages
import ListDrawings from "./pages/Drawing/ListDrawings";
import AddDrawing from "./pages/Drawing/AddDrawing";
import EditDrawing from "./pages/Drawing/EditDrawing";
import EditDrawingSiteDetail from "./pages/Drawing/EditDrawingSiteDetail";
import EditDrawingTakeOff from "./pages/Drawing/EditDrawingTakeOff";
import EditDrawingBom from "./pages/Drawing/EditDrawingBom";
import EditDrawingInstall from "./pages/Drawing/EditDrawingInstall";
import EditDrawingAdditionalOption from "./pages/Drawing/EditDrawingAdditionalOption";
import PurchaseOrders from "./pages/Drawing/PurchaseOrders";
import PurchaseOrderHistory from "./pages/Drawing/PurchaseOrderHistory";
import AddDrawingFiles from "./pages/Drawing/AddDrawingFiles";
import EditDrawingDiscount from "./pages/Drawing/EditDrawingDiscount";
import EditDrawingPandL from "./pages/Drawing/EditDrawingPandL";
import EditDrawingProposal from "./pages/Drawing/EditDrawingProposal";
import DrawingQuestions from "./pages/Drawing/DrawingQuestions";
import CommercialInvoice from "./pages/Drawing/CommercialInvoice";
import ExpenseSnags from "./pages/Drawing/ExpenseSnags";
import ExpenseSnagsList from "./pages/Drawing/ExpenseSnagsList";
import EditExpenseSnags from "./pages/Drawing/EditExpenseSnags";

// Settings Pages
import SettingList from "./pages/Settings/SettingList";
import UserList from "./pages/Settings/User/ListUser";
import AddUser from "./pages/Settings/User/AddUser";
import EditUser from "./pages/Settings/User/EditUser";
import ListDdOption from "./pages/Settings/DdOptions/ListDdOption";
import AddDdOption from "./pages/Settings/DdOptions/AddDdOption";
import EditDdOption from "./pages/Settings/DdOptions/EditDdOption";
import ListItem from "./pages/Settings/Items/ListItem";
import AddItem from "./pages/Settings/Items/AddItem";
import EditItem from "./pages/Settings/Items/EditItem";
import ListTakeOffItem from "./pages/Settings/Takeoff Items/ListTakeOffItem";
import AddTakeOffItem from "./pages/Settings/Takeoff Items/AddTakeOffItem";
import EditTakeOffItem from "./pages/Settings/Takeoff Items/EditTakeOffItem";
import ListTaxRate from "./pages/Settings/Finance/TaxRate/ListTaxRate";
import ListSupplier from "./pages/Settings/Supplier/ListSupplier";
import AddSupplier from "./pages/Settings/Supplier/AddSupplier";
import EditSupplier from "./pages/Settings/Supplier/EditSupplier";
import EditBillingShipping from "./pages/Settings/Supplier/EditBillingShipping";
import ListSupplierContact from "./pages/Settings/Supplier/ListSupplierContact";
import AddSupplierContact from "./pages/Settings/Supplier/AddSupplierContact";
import EditSupplierContact from "./pages/Settings/Supplier/EditSupplierContact";
import ListItemPrice from "./pages/Settings/ItemPrices/ListItemPrices";
import AddItemPrice from "./pages/Settings/ItemPrices/AddItemPrice";
import EditItemPrice from "./pages/Settings/ItemPrices/EditItemPrice";
import ListExpensePeriod from "./pages/Settings/ExpensePeriod/ListExpensePeriod";
import AddExpensePeriod from "./pages/Settings/ExpensePeriod/AddExpensePeriod";
import EditExpensePeriod from "./pages/Settings/ExpensePeriod/EditExpensePeriod";
import ProposalFiles from "./pages/Settings/ProposalFiles/ProposalFiles";
import ListOptionCosts from "./pages/Settings/OptionCosts/ListOptionCosts";
import AddOptionCosts from "./pages/Settings/OptionCosts/AddOptionCosts";
import EditOptionCosts from "./pages/Settings/OptionCosts/EditOptionCosts";
import Questions from "./pages/Settings/Questions/ListQuestions";
import AddQuestion from "./pages/Settings/Questions/AddQuestion";
import EditQuestion from "./pages/Settings/Questions/EditQuestion";

// Customer Pages
import ListCustomer from "./pages/Customer/ListCustomer";
import AddCustomer from "./pages/Customer/AddCustomer";
import EditCustomer from "./pages/Customer/EditCustomer";
import ListSite from "./pages/Customer/ListSite";
import AddSite from "./pages/Customer/AddSite";
import EditSite from "./pages/Customer/EditSite";
import ListCustomerContact from "./pages/Customer/ListCustomerContact";
import AddCustomerContact from "./pages/Customer/AddCustomerContact";
import EditCustomerContact from "./pages/Customer/EditCustomerContact";

// Expense & Finance Pages
import ListExpense from "./pages/Expenses/ListExpense";
import AddExpense from "./pages/Expenses/AddExpense";
import EditExpense from "./pages/Expenses/EditExpense";
import ListInvoices from "./pages/Invoices/ListInvoices";
import ListBills from "./pages/Bills/ListBills";
import Financials from "./pages/Financials/Financials";
import ListBankTransactions from "./pages/BankTransactions/ListBankTransactions";

// Mileage & Time Pages
import Mileage from "./pages/Mileage/Mileage";
import ListBusinessMileage from "./pages/BusinessMileage/ListBusinessMileage";
import AddBusinessMileage from "./pages/BusinessMileage/AddBusinessMileage";
import EditBusinessMileage from "./pages/BusinessMileage/EditBusinessMileage";
import ListCarMileage from "./pages/CarMileage/ListCarMileage";
import AddCarMileage from "./pages/CarMileage/AddCarMileage";
import EditCarMileage from "./pages/CarMileage/EditCarMileage";
import ListBusinessTimeEntry from "./pages/BusinessTimeEntry/ListBusinessTimeEntry";
import AddBusinessTimeEntry from "./pages/BusinessTimeEntry/AddBusinessTimeEntry";
import EditBusinessTimeEntry from "./pages/BusinessTimeEntry/EditBusinessTimeEntry";

// Reports
import Reports from "./pages/Reports/Reports";

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    const local_data = JSON.parse(localStorage.getItem("userSession"));
    if (local_data && local_data.token) {
      dispatch(setAuthUser(local_data));
    }
  }, [dispatch]);

  // --- IMPORTANT FIX HERE ---
  // Adding `key={screenCode}` forces the ProtectedRoute component to unmount and remount
  // whenever the screenCode changes. This ensures the "loading" state is triggered immediately.
  const ProtectedPage = ({ children, screenCode }) => (
    <ProtectedRoute screenCode={screenCode} key={screenCode}>
      <MainLayout>{children}</MainLayout>
    </ProtectedRoute>
  );

  return (
    <Routes>
      {/* --- PUBLIC ROUTES --- */}
      <Route path="/" element={<Login />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* --- PRIVATE ROUTES (AUTH CHECKED) --- */}
      <Route element={<PrivateRoutes />}>
        <Route path="/dashboard" element={<MainLayout><Dashboard /></MainLayout>} />
        {/* OPPORTUNITIES */}
        <Route path="/opportunity" element={<ProtectedPage screenCode="OPPORTUNITY_LIST_VIEW"><ListOpportunity /></ProtectedPage>} />
        <Route path="/opportunity/add" element={<ProtectedPage screenCode="OPPORTUNITY_ADD"><AddOpportunity /></ProtectedPage>} />
        <Route path="/opportunity/:id" element={<ProtectedPage screenCode="OPPORTUNITY_EDIT"><EditOpportunity /></ProtectedPage>} />
        <Route path="/opportunity-summary" element={<ProtectedPage screenCode="OPPORTUNITY_SUMMARY"><OpportunitySummary /></ProtectedPage>} />

        {/* DRAWINGS */}
        <Route path="/drawings" element={<ProtectedPage screenCode="DRAWING_LIST_VIEW"><ListDrawings /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/request" element={<ProtectedPage screenCode="DRAWING_ADD"><AddDrawing /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id" element={<ProtectedPage screenCode="DRAWING_EDIT"><EditDrawing /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/site" element={<ProtectedPage screenCode="DRAWING_SITE_EDIT"><EditDrawingSiteDetail /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/take-off" element={<ProtectedPage screenCode="DRAWING_TAKEOFF_EDIT"><EditDrawingTakeOff /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/bom" element={<ProtectedPage screenCode="DRAWING_BOM_EDIT"><EditDrawingBom /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/install" element={<ProtectedPage screenCode="DRAWING_INSTALL_EDIT"><EditDrawingInstall /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/additional-options" element={<ProtectedPage screenCode="DRAWING_OPTIONS_EDIT"><EditDrawingAdditionalOption /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/purchase-orders" element={<ProtectedPage screenCode="DRAWING_PO_VIEW"><PurchaseOrders /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/purchase-orders/history" element={<ProtectedPage screenCode="DRAWING_PO_HISTORY"><PurchaseOrderHistory /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/files" element={<ProtectedPage screenCode="DRAWING_FILES_EDIT"><AddDrawingFiles /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/discount" element={<ProtectedPage screenCode="DRAWING_DISCOUNT_EDIT"><EditDrawingDiscount /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/pandl" element={<ProtectedPage screenCode="DRAWING_PL_VIEW"><EditDrawingPandL /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/proposal" element={<ProtectedPage screenCode="DRAWING_PROPOSAL_EDIT"><EditDrawingProposal /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/questions" element={<ProtectedPage screenCode="DRAWING_QUESTIONS_VIEW"><DrawingQuestions /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/commercial-invoice" element={<ProtectedPage screenCode="DRAWING_INVOICE_VIEW"><CommercialInvoice /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/expense-snags" element={<ProtectedPage screenCode="DRAWING_SNAGS_ADD"><ExpenseSnags /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/expense-snags/edit/:snag_id" element={<ProtectedPage screenCode="DRAWING_SNAGS_EDIT"><EditExpenseSnags /></ProtectedPage>} />
        <Route path="/opportunity/:id/drawing/:drawing_id/expense-snags-list" element={<ProtectedPage screenCode="DRAWING_SNAGS_LIST"><ExpenseSnagsList /></ProtectedPage>} />

        {/* CUSTOMERS */}
        <Route path="/customers" element={<ProtectedPage screenCode="CUSTOMER_LIST_VIEW"><ListCustomer /></ProtectedPage>} />
        <Route path="/customer/add" element={<ProtectedPage screenCode="CUSTOMER_ADD"><AddCustomer /></ProtectedPage>} />
        <Route path="/customer/:id" element={<ProtectedPage screenCode="CUSTOMER_EDIT"><EditCustomer /></ProtectedPage>} />
        <Route path="/customer/:id/site" element={<ProtectedPage screenCode="CUSTOMER_SITE_LIST"><ListSite /></ProtectedPage>} />
        <Route path="/customer/:id/site/add" element={<ProtectedPage screenCode="CUSTOMER_SITE_ADD"><AddSite /></ProtectedPage>} />
        <Route path="/customer/:id/site/:site_id" element={<ProtectedPage screenCode="CUSTOMER_SITE_EDIT"><EditSite /></ProtectedPage>} />
        <Route path="/customer/:id/contacts" element={<ProtectedPage screenCode="CUSTOMER_CONTACT_LIST"><ListCustomerContact /></ProtectedPage>} />
        <Route path="/customer/:id/contact/add" element={<ProtectedPage screenCode="CUSTOMER_CONTACT_ADD"><AddCustomerContact /></ProtectedPage>} />
        <Route path="/customer/:id/contact/:contact_id" element={<ProtectedPage screenCode="CUSTOMER_CONTACT_EDIT"><EditCustomerContact /></ProtectedPage>} />

        {/* EXPENSES */}
        <Route path="/expenses" element={<ProtectedPage screenCode="EXPENSE_LIST_VIEW"><ListExpense /></ProtectedPage>} />
        <Route path="/expense/add" element={<ProtectedPage screenCode="EXPENSE_ADD"><AddExpense /></ProtectedPage>} />
        <Route path="/expense/:id" element={<ProtectedPage screenCode="EXPENSE_EDIT"><EditExpense /></ProtectedPage>} />

        {/* FINANCE & BANKING */}
        <Route path="/financials" element={<ProtectedPage screenCode="FINANCE_DASHBOARD"><Financials /></ProtectedPage>} />
        <Route path="/invoices" element={<ProtectedPage screenCode="FINANCE_INVOICE_LIST"><ListInvoices /></ProtectedPage>} />
        <Route path="/bills" element={<ProtectedPage screenCode="FINANCE_BILL_LIST"><ListBills /></ProtectedPage>} />
        <Route path="/bank-transactions" element={<ProtectedPage screenCode="FINANCE_BANK_VIEW"><ListBankTransactions /></ProtectedPage>} />

        {/* MILEAGE */}
        <Route path="/mileage" element={<ProtectedPage screenCode="MILEAGE_DASHBOARD"><Mileage /></ProtectedPage>} />
        <Route path="/business-mileage/add" element={<ProtectedPage screenCode="MILEAGE_BUSINESS_ADD"><AddBusinessMileage /></ProtectedPage>} />
        <Route path="/business-mileage/:id" element={<ProtectedPage screenCode="MILEAGE_BUSINESS_EDIT"><EditBusinessMileage /></ProtectedPage>} />
        <Route path="/car-mileage/add" element={<ProtectedPage screenCode="MILEAGE_CAR_ADD"><AddCarMileage /></ProtectedPage>} />
        <Route path="/car-mileage/:id" element={<ProtectedPage screenCode="MILEAGE_CAR_EDIT"><EditCarMileage /></ProtectedPage>} />

        {/* TIME ENTRIES */}
        <Route path="/time-entries" element={<ProtectedPage screenCode="TIME_ENTRY_LIST"><ListBusinessTimeEntry /></ProtectedPage>} />
        <Route path="/time-entries/add" element={<ProtectedPage screenCode="TIME_ENTRY_ADD"><AddBusinessTimeEntry /></ProtectedPage>} />
        <Route path="/time-entries/:id" element={<ProtectedPage screenCode="TIME_ENTRY_EDIT"><EditBusinessTimeEntry /></ProtectedPage>} />

        {/* REPORTS */}
        <Route path="reports" element={<ProtectedPage screenCode="REPORT_VIEW"><Reports /></ProtectedPage>} />

        {/* SETTINGS MODULE */}
        <Route path="/setting" element={<ProtectedPage screenCode="SETTING_VIEW"><SettingList /></ProtectedPage>} />
        <Route path="/setting/user" element={<ProtectedPage screenCode="SETTING_USER_LIST"><UserList /></ProtectedPage>} />
        <Route path="/setting/user/add" element={<ProtectedPage screenCode="SETTING_USER_ADD"><AddUser /></ProtectedPage>} />
        <Route path="/setting/user/:id" element={<ProtectedPage screenCode="SETTING_USER_EDIT"><EditUser /></ProtectedPage>} />
        <Route path="/setting/dd-option" element={<ProtectedPage screenCode="SETTING_DD_LIST"><ListDdOption /></ProtectedPage>} />
        <Route path="/setting/dd-option/add" element={<ProtectedPage screenCode="SETTING_DD_ADD"><AddDdOption /></ProtectedPage>} />
        <Route path="/setting/dd-option/:id" element={<ProtectedPage screenCode="SETTING_DD_EDIT"><EditDdOption /></ProtectedPage>} />
        <Route path="/setting/item" element={<ProtectedPage screenCode="SETTING_ITEM_LIST"><ListItem /></ProtectedPage>} />
        <Route path="/setting/item/add" element={<ProtectedPage screenCode="SETTING_ITEM_ADD"><AddItem /></ProtectedPage>} />
        <Route path="/setting/item/:id" element={<ProtectedPage screenCode="SETTING_ITEM_EDIT"><EditItem /></ProtectedPage>} />
        <Route path="/setting/takeoff/item" element={<ProtectedPage screenCode="SETTING_TAKEOFF_LIST"><ListTakeOffItem /></ProtectedPage>} />
        <Route path="/setting/takeoff/item/add" element={<ProtectedPage screenCode="SETTING_TAKEOFF_ADD"><AddTakeOffItem /></ProtectedPage>} />
        <Route path="/setting/takeoff/item/:id" element={<ProtectedPage screenCode="SETTING_TAKEOFF_EDIT"><EditTakeOffItem /></ProtectedPage>} />
        <Route path="/setting/finance/taxrate" element={<ProtectedPage screenCode="SETTING_TAX_LIST"><ListTaxRate /></ProtectedPage>} />
        <Route path="/setting/supplier" element={<ProtectedPage screenCode="SETTING_SUPPLIER_LIST"><ListSupplier /></ProtectedPage>} />
        <Route path="/setting/supplier/add" element={<ProtectedPage screenCode="SETTING_SUPPLIER_ADD"><AddSupplier /></ProtectedPage>} />
        <Route path="/setting/supplier/:id" element={<ProtectedPage screenCode="SETTING_SUPPLIER_EDIT"><EditSupplier /></ProtectedPage>} />
        <Route path="/setting/supplier/:id/billing" element={<ProtectedPage screenCode="SETTING_SUPPLIER_BILLING"><EditBillingShipping /></ProtectedPage>} />
        <Route path="/setting/supplier/:id/contact" element={<ProtectedPage screenCode="SETTING_SUPPLIER_CONTACT_LIST"><ListSupplierContact /></ProtectedPage>} />
        <Route path="/setting/supplier/:id/contact/add" element={<ProtectedPage screenCode="SETTING_SUPPLIER_CONTACT_ADD"><AddSupplierContact /></ProtectedPage>} />
        <Route path="/setting/supplier/:id/contact/:contact_id" element={<ProtectedPage screenCode="SETTING_SUPPLIER_CONTACT_EDIT"><EditSupplierContact /></ProtectedPage>} />
        <Route path="/setting/item-price" element={<ProtectedPage screenCode="SETTING_PRICE_LIST"><ListItemPrice /></ProtectedPage>} />
        <Route path="/setting/item-price/add" element={<ProtectedPage screenCode="SETTING_PRICE_ADD"><AddItemPrice /></ProtectedPage>} />
        <Route path="/setting/item-price/:id" element={<ProtectedPage screenCode="SETTING_PRICE_EDIT"><EditItemPrice /></ProtectedPage>} />
        <Route path="/setting/expense-period" element={<ProtectedPage screenCode="SETTING_EXP_PERIOD_LIST"><ListExpensePeriod /></ProtectedPage>} />
        <Route path="/setting/expense-period/add" element={<ProtectedPage screenCode="SETTING_EXP_PERIOD_ADD"><AddExpensePeriod /></ProtectedPage>} />
        <Route path="/setting/expense-period/:id" element={<ProtectedPage screenCode="SETTING_EXP_PERIOD_EDIT"><EditExpensePeriod /></ProtectedPage>} />
        <Route path="/setting/proposal-files" element={<ProtectedPage screenCode="SETTING_FILES_MGMT"><ProposalFiles /></ProtectedPage>} />
        <Route path="/setting/option-costs" element={<ProtectedPage screenCode="SETTING_COSTS_LIST"><ListOptionCosts /></ProtectedPage>} />
        <Route path="/setting/option-costs/add" element={<ProtectedPage screenCode="SETTING_COSTS_ADD"><AddOptionCosts /></ProtectedPage>} />
        <Route path="/setting/option-costs/:id" element={<ProtectedPage screenCode="SETTING_COSTS_EDIT"><EditOptionCosts /></ProtectedPage>} />
        <Route path="/setting/drawing-questions" element={<ProtectedPage screenCode="SETTING_QUESTIONS_LIST"><Questions /></ProtectedPage>} />
        <Route path="/setting/drawing-questions/add" element={<ProtectedPage screenCode="SETTING_QUESTIONS_ADD"><AddQuestion /></ProtectedPage>} />
        <Route path="/setting/drawing-questions/edit/:id" element={<ProtectedPage screenCode="SETTING_QUESTIONS_EDIT"><EditQuestion /></ProtectedPage>} />

      </Route>
    </Routes>
  );
}

export default App;